import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppShell } from './components/shell/AppShell';
import { HubAuthPage, LaunchPage } from './pages/AuthPages';
import { useSession, type Session } from './hooks/useSession';
import { useUi } from './store/ui';
import { Api } from './lib/api';
import { SessionContext } from './hooks/sessionContext';

const qc = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 5_000 },
  },
});

/**
 * Hash routes for deep links (the hub's deepLinkBase is "/#/note/"):
 *   #/note/<vault path or title>   #/types[/<key>]
 * Path routes: /hub-auth (launch handshake). Everything else is the shell.
 */
function useHashRoutes(enabled: boolean) {
  const openNote = useUi((s) => s.openNote);
  const setView = useUi((s) => s.setView);
  const toast = useUi((s) => s.toast);
  useEffect(() => {
    if (!enabled) return;
    const apply = async () => {
      const h = decodeURIComponent(location.hash.replace(/^#\/?/, ''));
      if (!h) return;
      if (h.startsWith('note/')) {
        const p = h.slice(5);
        try {
          const n = /\.md$/i.test(p) ? await Api.notes.byPath(p) : (await Api.notes.titles(p, 5)).find((t) => t.title.toLowerCase() === p.toLowerCase());
          if (n) openNote(n.id);
          else toast(`No note at ${p}`, 'warn');
        } catch {
          toast(`No note at ${p}`, 'warn');
        }
      } else if (h.startsWith('types')) {
        setView('types', h.split('/')[1] ?? null);
      }
      history.replaceState(null, '', location.pathname + location.search);
    };
    void apply();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
  }, [enabled, openNote, setView, toast]);
}

function Gate({ session }: { session: Session }) {
  const isHubAuth = location.pathname.replace(/\/$/, '') === '/hub-auth';
  useHashRoutes(!!session.user && !isHubAuth);
  if (isHubAuth) return <HubAuthPage onSession={session.establish} />;
  if (session.loading) return <div className="fullpage" />;
  if (!session.user) return <LaunchPage config={session.config} onSession={session.establish} />;
  return <AppShell />;
}

export default function App() {
  const session = useSession();
  return (
    <QueryClientProvider client={qc}>
      <SessionContext.Provider value={session}>
        <Gate session={session} />
      </SessionContext.Provider>
    </QueryClientProvider>
  );
}
